import React, { useMemo, useState } from 'react';
import type { UploadProps } from 'antd';
import { Input, message, Modal, Space, Typography, Upload } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import { API_URL } from '../../../../config';

type Props = {
  open : boolean;
  handleSetModalOpened: (opened: boolean) => void;
  handleGetData: () => void;
};

export const ModalAddTripWithFile: React.FC<Props> = ({ open, handleSetModalOpened, handleGetData}) => {
  const [trip, setTrip] = useState<string>("");
  const [fileList, setFileList] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const tripValid = useMemo(() => /^\d{6}$/.test(trip), [trip]);

  const MAX_SIZE_BYTES = 30000; // 30 KB
  // oficjalny MIME dla xlsx wg backendu
  const XLSX_MIME =
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

  const uploadProps: UploadProps = {
    multiple: false,
    maxCount: 1,
    fileList,
    accept: ".xlsx",
    beforeUpload: (file) => {
      // MIME + rozszerzenie (na wypadek dziwnych przeglądarek)
      const isXlsx =
        file.type === XLSX_MIME || file.name.toLowerCase().endsWith(".xlsx");
      if (!isXlsx) {
        message.error("Niepoprawny format pliku! Wgraj plik .xlsx");
        return Upload.LIST_IGNORE;
      }
      if (file.size > MAX_SIZE_BYTES) {
        message.error("Plik jest za duży! Maksymalnie 30 KB.");
        return Upload.LIST_IGNORE;
      }
      setFileList([file]); // zapisujemy, ale NIE wysyłamy automatycznie
      return false; // blokuje auto-upload — wyślemy ręcznie we fetchu
    },
    onRemove: () => {
      setFileList([]);
    },
    itemRender: (originNode) => originNode, // standardowy wygląd listy
  };

  const resetState = () => {
    setTrip("");
    setFileList([]);
  };

  const handleCancel = () => {
    if (!loading) {
      resetState();
      handleSetModalOpened(false);
    }
  };

  const handleOk = async () => {
    if (!tripValid) {
      message.error("Trip jest nieprawidłowy (wymagane 6 cyfr).");
      return;
    }
    if (fileList.length !== 1) {
      message.error("Dołącz plik .xlsx.");
      return;
    }

    const file = fileList[0]!.originFileObj ?? fileList[0];
    // Bez ustawiania Content-Type — przeglądarka sama ustawi boundary
    const formData = new FormData();
    formData.append("file", file);

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}ikea/trip/${trip}`, {
        method: "POST",
        body: formData,
        credentials: 'include',
        headers: {
          mode: 'cors',
        }
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        // Błąd HTTP — pokaż treść jeśli przyszła
        throw new Error(data?.error || "Wystąpił błąd podczas wysyłania.");
      }

      if (data?.success) {
        message.success("Trip dodany poprawnie.");
        resetState();
        handleSetModalOpened(false);
      } else if (data?.error) {
        // Obsługa komunikatów z backendu (walidacje: trip, duplikat, MIME, rozmiar itd.)
        message.error(String(data.error));
      } else {
        message.warning("Brak jednoznacznej odpowiedzi z serwera.");
      }
    } catch (e: any) {
      message.error(e?.message || "Nie udało się dodać tripa.");
    } finally {
      setLoading(false);
      handleGetData();
    }
  };

  return (
    <Modal
      title="Dodawanie Tripa"
      open={open}
      onOk={handleOk}
      onCancel={handleCancel}
      confirmLoading={loading}
      okButtonProps={{ disabled: !tripValid || fileList.length !== 1 }}
      bodyStyle={{ padding: 10 }}
      destroyOnClose
    >
      <Space direction="vertical" style={{ width: "100%" }} size="middle">
        <div>
          <Typography.Text strong>Nr tripa: </Typography.Text>
          <Input
            value={trip}
            onChange={(e) => setTrip(e.target.value.trim())}
            onPressEnter={handleOk}
            placeholder="Nr tripa"
            style={{ width: 220, marginTop: 6 }}
            status={trip.length > 0 && !tripValid ? "error" : undefined}
          />
        </div>

        <div>
          <Typography.Text strong>Pobieranie pliku z SAP</Typography.Text>
          <p>
            <Typography.Text>
              1. Transakcja VL06F<br/>
              2. Wariant shipbytrip<br/>
              3. Wpisanie tripa <br />
              4. Zegarek <br />
              5. Wgląd pozycji <br />
              6. Export {'->'} Arkusz Kalkulacyjny <br />
            </Typography.Text>
          </p>
          <Upload.Dragger {...uploadProps} style={{ marginTop: 6 }}>
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              Przeciągnij tutaj plik lub kliknij, aby wybrać
            </p>
            <p className="ant-upload-hint">Wymagany format: .xlsx, max 30 KB</p>
          </Upload.Dragger>
        </div>
      </Space>
    </Modal>
  );
};
